import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Grid } from "@mui/material";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import ApiDatePickerField from "../../../components/date/ApiDatePickerField";
import {
  resolveServiceGroupForRole,
  filterClassificationsForGroup,
  DIAGNOSTIC_DOCTOR_ROLES,
  CLASSIFICATION_ALL,
  SALUTATIONS,
  getDistricts,
} from "../api/diagnosticDoctorApi";
import { addDiagnosticUserViaNewUser } from "../api/userManagementDiagnosticApi";

const GENDERS = [
  { value: "M", label: "Male" },
  { value: "F", label: "Female" },
  { value: "T", label: "Transgender" },
];

function DiagnosticDoctorAddition({ updateList, lookups = { groups: [], classifications: [] }, states = [] }) {
  const {
    register,
    watch,
    setValue,
    trigger,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      active: true,
      service_classification_id: CLASSIFICATION_ALL,
    },
  });

  const qualificationsValue = watch("qualifications", "");
  const roleType = watch("role_type", "");
  const serviceGroupId = watch("service_group_id", "");
  const { onChange: onStateChange, ...stateRegisterRest } = register("state");

  const [districts, setDistricts] = useState([]);
  const [attachments, setAttachments] = useState(null);

  useEffect(() => {
    if (!roleType) {
      setValue("service_group_id", "");
      setValue("service_classification_id", CLASSIFICATION_ALL);
      return;
    }
    const matched = resolveServiceGroupForRole(roleType, lookups.groups);
    setValue("service_group_id", matched?.id ? String(matched.id) : "");
    setValue("service_classification_id", CLASSIFICATION_ALL);
  }, [roleType, lookups.groups, setValue]);

  useEffect(() => {
    setValue("service_classification_id", CLASSIFICATION_ALL);
  }, [serviceGroupId, setValue]);

  const groupClassifications = useMemo(
    () =>
      filterClassificationsForGroup(
        lookups.classifications,
        serviceGroupId ? Number(serviceGroupId) : null
      ),
    [lookups.classifications, serviceGroupId]
  );

  const handleFileSelect = (acceptedFiles) => {
    Promise.all(
      acceptedFiles.map(
        (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: file.type,
                size: file.size,
                base64: reader.result,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })
      )
    )
      .then((newAttachments) => setAttachments(newAttachments))
      .catch((err) => console.error("Error reading signature:", err));
  };

  const handleStateChange = (stateName) => {
    setDistricts([]);
    setValue("district", "");
    setValue("city", "");
    if (!stateName) return;
    getDistricts(stateName)
      .then(setDistricts)
      .catch((err) => console.error("getDistricts:", err));
  };

  const onSubmit = async (data, e) => {
    e.preventDefault();

    if (!data.service_group_id) {
      toast.error("Service Group is required for the selected role");
      return;
    }
    if (!String(data.gender || "").trim()) {
      toast.error("Gender is required");
      return;
    }

    try {
      const res = await addDiagnosticUserViaNewUser(
        {
          ...data,
          user_type: data.role_type,
        },
        attachments,
      );
      if (res?.status === "Success") {
        toast.success(res?.data?.message || res?.message || "Diagnostic doctor added");
        reset({
          role_type: "",
          salutation: "",
          first_name: "",
          last_name: "",
          username: "",
          password: "",
          email: "",
          phone_no: "",
          registration_no: "",
          specialization: "",
          qualifications: "",
          gender: "",
          state: "",
          district: "",
          city: "",
          service_group_id: "",
          service_classification_id: CLASSIFICATION_ALL,
          active: true,
        });
        setDistricts([]);
        setAttachments(null);
        updateList();
      } else {
        toast.error(res?.message || "Could not add diagnostic doctor");
      }
    } catch (err) {
      console.error("addDiagnosticUserViaNewUser:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not add diagnostic doctor"
      );
    }
  };

  return (
    <div>
      <div className="col-sm-12 col-md-12 col-lg-10" style={{ margin: "auto" }}>
        <div className="contact-panel mb-50" style={{ marginTop: "50px" }}>
          <Grid container alignItems="center">
            <Grid item xs={6}>
              <h4 style={{ textAlign: "left" }}>Add Diagnostic Doctor</h4>
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={4} style={{ textAlign: "left" }}>
              <img
                src="/assets/images/newIcons/patienticon.svg"
                alt="Profile"
                style={{
                  width: "clamp(60px, 15vw, 120px)",
                  height: "auto",
                  borderRadius: "50%",
                  marginLeft: "40px",
                  objectFit: "cover",
                }}
              />
            </Grid>
          </Grid>

          <form
            className="contact-panel__form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <h5 style={{ textAlign: "left", marginBottom: "16px" }}>
                  Doctor Details
                </h5>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="role_type" className="required-label">
                  Role
                </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    {...register("role_type", { required: "Role is required." })}
                  >
                    <option value="">Select Role</option>
                    {DIAGNOSTIC_DOCTOR_ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {errors.role_type && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.role_type.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="salutation">Salutation</label>
                <div className="form-group">
                  <select className="form-control" {...register("salutation")}>
                    <option value="">Select Salutation</option>
                    {SALUTATIONS.map((title) => (
                      <option key={title} value={title}>
                        {title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="username" className="required-label">
                  User Name
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter User Name"
                    autoComplete="off"
                    {...register("username", {
                      required: "User Name is required.",
                    })}
                  />
                  {errors.username && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.username.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="password" className="required-label">
                  Password
                </label>
                <div className="form-group">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter Password"
                    autoComplete="new-password"
                    {...register("password", {
                      required: "Password is required.",
                    })}
                  />
                  <small style={{ color: "#6c757d" }}>
                    Min 8 characters with alphabet, number, and special character.
                  </small>
                  {errors.password && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="first_name" className="required-label">
                  First Name
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter First Name"
                    {...register("first_name", {
                      required: "First Name is required.",
                    })}
                  />
                  {errors.first_name && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="last_name" className="required-label">
                  Last Name
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Last Name"
                    {...register("last_name", {
                      required: "Last Name is required.",
                    })}
                  />
                  {errors.last_name && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="phone_no" className="required-label">
                  Phone No.
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Phone Number"
                    {...register("phone_no", {
                      required: "Phone Number is required.",
                    })}
                  />
                  {errors.phone_no && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.phone_no.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="email" className="required-label">
                  Email
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Email"
                    {...register("email", {
                      required: "Email is required.",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="qualifications" className="required-label">
                  Qualification
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Qualifications"
                    maxLength={170}
                    {...register("qualifications", {
                      required: "Qualifications is required.",
                      maxLength: {
                        value: 170,
                        message: "Maximum 170 characters allowed.",
                      },
                    })}
                    onChange={(e) => {
                      setValue("qualifications", e.target.value);
                      trigger("qualifications");
                    }}
                  />
                  <div
                    style={{
                      textAlign: "right",
                      fontSize: "12px",
                      color: "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    {(qualificationsValue || "").length}/170
                  </div>
                  {errors.qualifications && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.qualifications.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="specialization" className="required-label">
                  Specialization
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Specialization"
                    {...register("specialization", {
                      required: "Specialization is required.",
                    })}
                  />
                  {errors.specialization && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.specialization.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="registration_no" className="required-label">
                  Medical Registration No
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Medical Registration No"
                    {...register("registration_no", {
                      required: "Registration Number is required.",
                    })}
                  />
                  {errors.registration_no && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.registration_no.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="service_group_id" className="required-label">
                  Service Group
                </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    {...register("service_group_id", {
                      required: "Service Group is required.",
                    })}
                  >
                    <option value="">Select Service Group</option>
                    {lookups.groups.map((group) => (
                      <option key={group.id} value={String(group.id)}>
                        {group.name} ({group.code})
                      </option>
                    ))}
                  </select>
                  {errors.service_group_id && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.service_group_id.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="service_classification_id">
                  Service Classification
                </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    disabled={!serviceGroupId}
                    {...register("service_classification_id")}
                  >
                    <option value={CLASSIFICATION_ALL}>All</option>
                    {groupClassifications.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.name} ({item.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="row" style={{ marginTop: "8px" }}>
              <div className="col-12">
                <h5 style={{ textAlign: "left", marginBottom: "16px" }}>
                  Other Details
                </h5>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="dob">Date of Birth</label>
                <div className="form-group">
                  <Controller
                    name="dob"
                    control={control}
                    render={({ field }) => (
                      <ApiDatePickerField
                        id="diagnostic-add-dob"
                        value={field.value || ""}
                        onChange={field.onChange}
                        className="form-control"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="date_joined">Practice started since</label>
                <div className="form-group">
                  <Controller
                    name="date_joined"
                    control={control}
                    render={({ field }) => (
                      <ApiDatePickerField
                        id="diagnostic-add-date-joined"
                        value={field.value || ""}
                        onChange={field.onChange}
                        className="form-control"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="gender" className="required-label">
                  Gender
                </label>
                <div className="form-group">
                  <select
                    className="form-control"
                    {...register("gender", {
                      required: "Gender is required.",
                    })}
                  >
                    <option value="">Select Gender</option>
                    {GENDERS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                  {errors.gender && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.gender.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="state">State</label>
                <select
                  className="form-control"
                  {...stateRegisterRest}
                  onChange={(e) => {
                    onStateChange(e);
                    handleStateChange(e.target.value);
                  }}
                >
                  <option value="">Select State</option>
                  {states.map((state, index) => (
                    <option key={index} value={state.state_name.toLowerCase()}>
                      {state.state_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="district">District</label>
                <div className="form-group">
                  <select
                    className="form-control"
                    {...register("district", {
                      onChange: (e) => {
                        setValue("city", e.target.value);
                      },
                    })}
                  >
                    <option value="">Select District</option>
                    {districts.map((district, index) => (
                      <option
                        key={index}
                        value={district.district_name.toLowerCase()}
                      >
                        {district.district_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-sm-6 col-md-6 col-lg-6">
                <label htmlFor="city">City</label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter City"
                    {...register("city")}
                  />
                </div>
              </div>

              <div className="col-sm-12 col-md-12 col-lg-12">
                <label htmlFor="address" className="required-label">
                  Address
                </label>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter full address"
                    {...register("address", {
                      required: "Address is required.",
                    })}
                  />
                  {errors.address && (
                    <p className="errorMsg" style={{ color: "red" }}>
                      {errors.address.message}
                    </p>
                  )}
                </div>
              </div>

              <div
                className="col-sm-6 col-md-6 col-lg-6"
                style={{ marginTop: "10px" }}
              >
                <label htmlFor="doctor_signature">Doctor Signature</label>
                <Controller
                  name="doctor_signature"
                  control={control}
                  defaultValue={[]}
                  rules={{
                    validate: (files) => {
                      if (!files || files.length === 0) return true;
                      const file = files[0];
                      if (file.size < 20 * 1024) {
                        return "File size must be at least 20KB.";
                      }
                      if (file.size > 2 * 1024 * 1024) {
                        return "File size must not exceed 2MB.";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <div>
                      {field.value && field.value.length > 0 ? (
                        <div
                          style={{
                            position: "relative",
                            width: "40%",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={URL.createObjectURL(field.value[0])}
                            alt="Uploaded Signature"
                            style={{ width: "100%", objectFit: "contain" }}
                          />
                          <button
                            type="button"
                            style={{
                              position: "absolute",
                              top: "5px",
                              right: "5px",
                              background: "red",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              field.onChange([]);
                              setAttachments(null);
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      ) : (
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            handleFileSelect(acceptedFiles);
                            field.onChange(acceptedFiles);
                          }}
                          maxFiles={1}
                          accept={{ "image/*": [".jpeg", ".jpg", ".png"] }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div
                              {...getRootProps({
                                className: "dropzone",
                                style: {
                                  border: "2px dashed #ccc",
                                  padding: "3px",
                                  cursor: "pointer",
                                },
                              })}
                            >
                              <input {...getInputProps()} />
                              <p style={{ margin: 0 }}>
                                Drag & drop a file here, or click to select
                              </p>
                            </div>
                          )}
                        </Dropzone>
                      )}
                      {errors.doctor_signature && (
                        <p className="errorMsg" style={{ color: "red" }}>
                          {errors.doctor_signature.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>

              <div
                className="col-sm-3 col-md-3 col-lg-3"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "3.9%",
                }}
              >
                <div className="form-group">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      {...register("active")}
                    />
                    <label className="form-check-label">Active</label>
                  </div>
                </div>
              </div>

              <div
                className="col-sm-3 col-md-3 col-lg-3"
                style={{ marginTop: "1.02%" }}
              >
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn__secondary btn__rounded btn__block btn__xhight mt-10"
                >
                  <span>Add Diagnostic Doctor</span>
                  <i className="icon-arrow-right"></i>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default DiagnosticDoctorAddition;
